import React, { useCallback, useState } from "react";
import { Alert } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import GenericDialog from "@components/GenericDialog/GenericDialog";
import Select from "@components/Select/Select";
import { getSideDrawerStyles } from "@components/SideDrawer/SideDrawer.styles";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { SIDE_DRAWER_MAX_WIDTH } from "@constants/layout.style";
import { useNotification } from "@context/NotificationContext";
import { validityPeriodStringToInt } from "@features/port-security/components/utils";
import {
  bulkCompleteSecurityClearance,
  SECURITY_CLEARANCE_BULK_MAX,
} from "@services/securityClearanceApi";
import { useMutation } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { useTranslation } from "react-i18next";
import {
  BULK_SC_SERVICE,
  notifyBulkDrawerError,
  notifyBulkDrawerOutcome,
  notifyBulkSelectionLimit,
  type BulkSecurityClearanceRow,
  type SecurityClearanceError,
} from "./bulkSecurityClearanceDrawerShared";

export type { BulkSecurityClearanceRow };

interface BulkApproveSecurityClearanceDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  selectedRows: BulkSecurityClearanceRow[];
  /**
   * Supervisors approving at the supervisor approval stage must provide a validity period.
   * Bulk verify (operators / dept-stage) uses a confirm dialog instead of this drawer.
   */
  requireValidityPeriod?: boolean;
  validityPeriodOptions?: { label: string; value: string }[];
  onSuccess?: () => void;
}

const BulkApproveSecurityClearanceDrawer: React.FC<BulkApproveSecurityClearanceDrawerProps> = ({
  isOpen,
  onClose,
  selectedRows,
  requireValidityPeriod = false,
  validityPeriodOptions = [],
  onSuccess,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const styles = getSideDrawerStyles(theme);
  const { notify } = useNotification();

  const [validityPeriod, setValidityPeriod] = useState("");
  const [errors, setErrors] = useState<{ validityPeriod?: string }>({});
  const [error] = useState<null | string>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const resetFormValues = useCallback(() => {
    setValidityPeriod("");
    setErrors({});
    setShowConfirmDialog(false);
  }, []);

  const bulkCompleteMutation = useMutation({
    mutationFn: async () =>
      runTransaction(
        {
          module: "security_clearance",
          screen: "Security Clearance Bulk Approval",
          action: "bulk_approve_security_clearances",
          entityType: "security_clearance",
        },
        async (transactionId) => {
          const items = selectedRows.map((row) => ({ securityClearanceId: row.id }));

          const taskDecision: {
            status: "APPROVED";
            validityPeriod?: number;
          } = { status: "APPROVED" };

          if (requireValidityPeriod) {
            taskDecision.validityPeriod = validityPeriodStringToInt(validityPeriod);
          }

          return bulkCompleteSecurityClearance(
            BULK_SC_SERVICE,
            { items, taskDecision },
            ...(transactionId ? [transactionId] : []),
          );
        },
      ),
    onSuccess: async (data) => {
      onSuccess?.();
      onClose();
      resetFormValues();
      notifyBulkDrawerOutcome(notify, t, data, selectedRows.length, {
        partialTitle: t("security-clearance.bulk-cid-approve-partial", {
          defaultValue: "Bulk CID approve completed with errors",
        }),
        partialDetailKey: "security-clearance.bulk-cid-approve-partial-detail",
        partialDetailDefault: "{{success}} approved, {{failed}} failed.",
        successTitle: t("security-clearance.security-clearance-request-cid-approved", {
          defaultValue: "Security Clearance has been CID Approved!",
        }),
      });
    },
    onError: async (err: SecurityClearanceError) => {
      setShowConfirmDialog(false);
      notifyBulkDrawerError(notify, t, err);
    },
  });

  const handleSubmit = useCallback(() => {
    if (selectedRows.length > SECURITY_CLEARANCE_BULK_MAX) {
      notifyBulkSelectionLimit(notify, t);
      return;
    }

    if (requireValidityPeriod && !validityPeriod) {
      setErrors({
        validityPeriod: t("form-validation.required", {
          label: t("security-clearance.validity-period", { defaultValue: "Validity period" }),
          defaultValue: "This field is required",
        }),
      });
      return;
    }

    setErrors({});
    setShowConfirmDialog(true);
  }, [notify, requireValidityPeriod, selectedRows.length, t, validityPeriod]);

  const handleConfirmDialogConfirm = useCallback(() => {
    bulkCompleteMutation.mutate();
  }, [bulkCompleteMutation]);

  const handleConfirmDialogClose = useCallback(() => {
    setShowConfirmDialog(false);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    onClose();
    resetFormValues();
  }, [onClose, resetFormValues]);

  if (selectedRows.length === 0) return null;

  return (
    <div
      data-testid="bulk-approve-security-clearance-drawer-form"
      className="max-h-screen overflow-y-auto"
    >
      <SideDrawer
        isOpen={isOpen}
        title={t("security-clearance.bulk-approve-drawer-title", {
          defaultValue: "Bulk Approve Security Clearance",
        })}
        onClose={handleCloseDrawer}
        submitLabel={t("security-clearance.bulk-approve-button")}
        onSubmit={handleSubmit}
        isSubmitting={bulkCompleteMutation.isPending}
        width={SIDE_DRAWER_MAX_WIDTH}
      >
        <div data-testid="bulk-approve-security-clearance-drawer-content" className="flex-1 w-full">
          <div className="px-6">
            <div className="mt-0 mb-2">
              <p
                data-testid="bulk-approve-security-clearance-form-title"
                style={styles.descriptionText}
              >
                {t("security-clearance.bulk-approve-drawer-message", {
                  defaultValue: "You are about to bulk approve Security Clearance",
                })}
              </p>
              {selectedRows.map((row) => (
                <span key={row.id} style={styles.idText}>
                  #{row.serialNo}.
                  <br />
                </span>
              ))}
            </div>
            {requireValidityPeriod && (
              <div className="col-span-full mb-0 flex flex-col gap-2 mt-6">
                <CustomFormLabel>
                  {t("security-clearance.validity-period", { defaultValue: "Validity period" })}
                </CustomFormLabel>
                <Select
                  testId="bulk-approve-security-clearance-validity-period"
                  options={validityPeriodOptions}
                  value={validityPeriod}
                  placeholder={t("security-clearance.select-validity-period", {
                    defaultValue: "Select validity period",
                  })}
                  error={Boolean(errors.validityPeriod)}
                  onChange={(value) => {
                    setValidityPeriod(value);
                    setErrors({});
                  }}
                />
                {errors.validityPeriod && (
                  <p style={styles.errorText} className="text-sm mt-1">
                    {errors.validityPeriod}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </SideDrawer>
      <GenericDialog
        open={showConfirmDialog}
        title={t("security-clearance.bulk-approve-confirm-title", {
          defaultValue: "Confirm bulk approve",
        })}
        elementContent={
          <>
            {t("security-clearance.bulk-approve-confirm-message", {
              defaultValue: "You are about to approve {{count}} security clearance request(s).",
              count: selectedRows.length,
            })}
            <br />
            {t("security-clearance.bulk-confirm-question", {
              defaultValue: "Are you sure you want to proceed?",
            })}
          </>
        }
        cancelButtonText={t("common.cancel")}
        confirmButtonText={t("common.confirm")}
        handleCloseDialog={handleConfirmDialogClose}
        confirmAction={handleConfirmDialogConfirm}
        testId="bulk-approve-security-clearance-confirm-dialog"
        isInsideDrawer
      />
      {error ? <Alert severity="error">{error}</Alert> : null}
    </div>
  );
};

export default BulkApproveSecurityClearanceDrawer;
